import { FC, useCallback, useState } from "react";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useApiClient } from "@hostingbv/lib/src/api";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { ModalFooter } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { Customer } from "@hostingbv/lib/src/api/common/models/Customer";
import { Contact } from "@hostingbv/lib/src/api/common/models/Contact";
import { RoleSelect } from "../components/RoleSelect";
import { config } from "../config";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";

export const EditCustomerContactRoleModal: FC<{ customer: Customer, contact: Contact }> = props => {
  const { partnerClient } = useApiClient(config)
  const [roleUuid, setRoleUuid] = useState('')
  const [error, setError] = useState('')
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!

  // ModalFooter refreshes and closes once this resolves, so wait until the role is actually changed.
  const save = useCallback(async () => {
    if (roleUuid === '') {
      throw new Error('No role selected')
    }
    const result = await partnerClient.updateCustomerContactUserRole(props.customer.id, props.contact.id, roleUuid)
    if (!result.success) {
      setError(result.error)
      throw new Error(result.error)
    }
    const deployment = await awaitNextDeploymentFinished(result.data.id)
    if (deployment?.status === "failed") {
      // @ts-expect-error output is untyped
      setError(deployment.output?.message ?? 'Could not change the role')
      throw new Error('deployment failed')
    }
  }, [partnerClient, roleUuid, props.customer, props.contact, awaitNextDeploymentFinished])

  return <>
    <div className={"space-y-4 flex-1"}>
      <Paragraph>
        The role determines what <strong>{props.contact.email}</strong> can see and do. It belongs to the login, not
        only to this contact. The change takes effect the next time the user logs in.
      </Paragraph>
      {props.contact.sharesUserWithOtherCustomers && <Callout color={"warning"} icon={faTriangleExclamation}>
        This login is also a contact at other customers. The role also applies to this login at those customers.
      </Callout>}
      <RoleSelect value={roleUuid} onChange={setRoleUuid} preselect={props.contact.role?.id} />
      {error.length > 0 && <div className={"font-medium text-red-600"}>
        {error}
      </div>}
    </div>
    <ModalFooter text={"Save"} onSubmit={save} />
  </>
}
